<template>
  <v-container>
    <Loader v-if="loading" :loading="loading" />

    <div class="page-grid-wrapper" v-else>
      <div class="header-wrapper">
        <div class="boxed-image standout lg">
          <img :src="product.logo" />
        </div>

        <div class="contact-wrapper">
          <div v-if="product.discount" class="discount">
            <img :src="discountIcon" />
            <label>LendingWell Benefit</label>
            <span>{{ product.discount }}</span>
          </div>

          <div class="button-group">
            <button
              v-if="!hasBeenContacted"
              class="default-btn blue"
              :disabled="emailingLender || !canUseContactButtons"
              @click="onContactLenderClicked"
            >Email Lender</button>

            <button
              v-else
              :disabled="!canUseContactButtons || hasBeenContacted"
              class="default-btn green"
            ><span class="mdi mdi-check"></span> Contacted</button>

            <p class="subdued">
              *Selecting "email lender" will send an email to lenders with your contact information and loan request details
            </p>
          </div>
        </div>
      </div>

      <div v-if="$vuetify.breakpoint.lgAndUp" class="score-wrapper">
        <star-rating
          :rating="product.scoreAverage"
          :count="product.reviewCount"
          :editable="false" />
      </div>

      <div class="description-wrapper">
        <DescriptionCard :lender="product" />
      </div>

      <ScoreTable :lender="product" />

      <CustomerReviews
        :dealId="dealId"
        :hasBeenContacted="hasBeenContacted"
        :lender="product" />

      <div v-if="product.disclosure" class="disclosure-wrapper">
        <p class="subdued">{{ product.disclosure }}</p>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import DiscountIcon from '@/modules/shared/assets/discount_icon.png'

import CustomerReviews from './CustomerReviews'
import DescriptionCard from './DescriptionCard'
import ScoreTable from './ScoreTable'

export default {
  name: 'Lender',
  props: {
    productId: String,
    dealId: String,
  },
  components: {
    CustomerReviews,
    DescriptionCard,
    ScoreTable,
  },
  data: () => ({
    product: null,
    matches: [],
    starReviews: [],
    loading: true,
    didContact: false,
    emailingLender: false,
    discountIcon: DiscountIcon,
  }),
  computed: {
    ...mapGetters('feathers', ['apiMatches', 'apiProducts']),

    canUseContactButtons () {
      return this.product && this.dealId && this.product.matches.length
    },

    hasBeenContacted () {
      return this.didContact || (
        this.product && this.product.matches && this.product.matches.length &&
        this.product.matches.some(({ contactedAt }) => contactedAt)
      )
    },
  },

  async mounted () {
    await this.getlender()
    this.loading = false
  },

  methods: {
    async getlender () {
      try {
        const matchesQuery = this.dealId
          ? { dealId: this.dealId }
          : {}

        this.product = await this.apiProducts.get(this.productId, {
          query: {
            $include: { Matches: { query: matchesQuery } },
          },
        })
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `There was an error loading the lender data: ${error.message}`,
        })
      }

      if (this.product) {
        document.title = `Match | ${this.product.title}`
        this.$gtag.pageview({ page_path: document.title })
      }
    },

    async onContactLenderClicked () {
      this.emailingLender = true
      try {
        await this.apiMatches
        await this.apiMatches.patch(this.product.matches[0].id, {
          contactedAt: Date.now(),
        })

        this.didContact = true
        this.addAlert({
          type: 'success',
          message: 'Successfully sent a contact email!',
        })
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `There was an error contacting the lender: ${error.message}`,
        })
      }

      this.emailingLender = false
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;
}

.page-grid-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: auto;
  grid-column-gap: 45px;
  grid-row-gap: 50px;

  @include lg {
    grid-template-columns: auto 1fr;
  }

  @include xl {
    grid-column-gap: 80px;
  }
}

.header-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 30px;

  .boxed-image { margin: 0 0 50px; }

  @include md {
    flex-direction: row;
    align-items: flex-end;

    .boxed-image { margin: 0; }
  }

  @include lg {
    @include gridPos($colS: 1, $colE: 3);

    margin-bottom: 0;
  }
}

.contact-wrapper {
  max-width: 400px;
  margin-bottom: -30px;
  padding: 0 20px;

  .button-group {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
    grid-column-gap: 30px;
    grid-row-gap: 20px;

    .default-btn {
      width: 200px;
      margin: auto;
    }

    p {
      text-align: center;
    }

    @include lg {
      grid-template-columns: 1fr;
    }
  }
}

.score-wrapper {
  @include lg {
    @include gridPos($colS: 1, $colE: 3);
  }

  margin-top: -40px;

  .rating-wrapper {
    width: 200px;
  }
}

.description-wrapper {
  @include lg {
    @include gridPos($colS: 1, $colE: 3);
  }
}

.disclosure-wrapper {
  @include lg {
    @include gridPos($colS: 1, $colE: 3);
  }

  text-align: center;
}

.discount {
  margin: 0 0 30px;
}
</style>
